import React, { useEffect, useRef, useState } from 'react';
import { useCountries } from '../hooks/useCountries';

const normalizeCountryId = (id) => String(id || '').trim();

/**
 * Multi-country picker. When `countries` is omitted, loads from GET /api/countries
 * (Vughy public countries via VUGHY_API_KEY on the server).
 */
const CountryMultiSelect = ({
  label,
  value = [],
  onChange,
  countries: countriesProp,
  loading: loadingProp = false,
  placeholder = 'Click to select countries',
  autoLoad = countriesProp === undefined,
}) => {
  const { countries: fetchedCountries, loading: fetching } = useCountries({
    enabled: autoLoad,
  });

  const countries = countriesProp !== undefined ? countriesProp : fetchedCountries;
  const loading = Boolean(loadingProp || (autoLoad && fetching));

  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const containerRef = useRef(null);
  const searchRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
        setSearch('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    const timer = window.setTimeout(() => {
      searchRef.current?.focus();
      searchRef.current?.select?.();
    }, 0);
    return () => window.clearTimeout(timer);
  }, [open]);

  const selectedIds = value.map(normalizeCountryId).filter(Boolean);
  const searchTerm = search.trim().toLowerCase();

  const getCountryName = (id) => {
    const country = countries.find(
      (item) => normalizeCountryId(item.id) === normalizeCountryId(id)
    );
    return country?.name?.trim() || id;
  };

  const filteredCountries = countries.filter((country) =>
    String(country.name || '').toLowerCase().includes(searchTerm)
  );

  const toggleCountry = (id) => {
    const normalized = normalizeCountryId(id);
    if (selectedIds.includes(normalized)) {
      onChange(selectedIds.filter((item) => item !== normalized));
    } else {
      onChange([...selectedIds, normalized]);
    }
  };

  const removeCountry = (id, e) => {
    e.stopPropagation();
    const normalized = normalizeCountryId(id);
    onChange(selectedIds.filter((item) => item !== normalized));
  };

  return (
    <div ref={containerRef} className="relative">
      {label && (
        <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
      )}

      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        disabled={loading}
        className="w-full min-h-[42px] px-3 py-2 border border-gray-200 rounded-xl text-sm text-left bg-white hover:border-gray-300 transition-colors focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent disabled:bg-gray-50 disabled:cursor-not-allowed"
      >
        {loading ? (
          <span className="text-gray-400">Loading countries...</span>
        ) : selectedIds.length === 0 ? (
          <span className="text-gray-400">{placeholder}</span>
        ) : (
          <div className="flex flex-wrap gap-1.5">
            {selectedIds.map((id) => (
              <span
                key={id}
                className="inline-flex items-center gap-1 bg-gray-100 text-gray-700 px-2 py-0.5 rounded-lg text-xs font-medium"
              >
                {getCountryName(id)}
                <span
                  role="button"
                  tabIndex={0}
                  onClick={(e) => removeCountry(id, e)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      removeCountry(id, e);
                    }
                  }}
                  className="text-gray-400 hover:text-gray-600 leading-none"
                >
                  ×
                </span>
              </span>
            ))}
          </div>
        )}
      </button>

      {open && !loading && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-xl shadow-lg overflow-hidden">
          <div className="p-2 border-b border-gray-100">
            <input
              ref={searchRef}
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search countries..."
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent"
              onKeyDown={(e) => {
                if (e.key === 'Escape') {
                  setOpen(false);
                  setSearch('');
                }
              }}
            />
          </div>

          <div className="max-h-48 overflow-y-auto">
            {filteredCountries.length === 0 ? (
              <p className="px-3 py-4 text-sm text-gray-400 text-center">No countries found</p>
            ) : (
              filteredCountries.map((country) => {
                const id = normalizeCountryId(country.id);
                const isSelected = selectedIds.includes(id);

                return (
                  <button
                    key={id}
                    type="button"
                    onClick={() => toggleCountry(id)}
                    className={`w-full flex items-center justify-between px-3 py-2.5 text-sm text-left transition-colors ${
                      isSelected ? 'bg-primary text-white' : 'text-ink-table hover:bg-primary-soft'
                    }`}
                  >
                    <span>{country.name}</span>
                    {isSelected && <span className="text-xs font-medium">Selected</span>}
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default CountryMultiSelect;
